/**
 * useLiveSync — subscribe a learner / preview page to live admin edits.
 *
 * The backend exposes a Server-Sent-Events stream per entity. Whenever an
 * admin saves a module or course, the relevant topic fans the change out to
 * every open browser tab subscribed to that topic. The `onUpdate` callback
 * fires within ~50ms — typically before the admin's "Save" toast clears.
 *
 * Usage:
 *   useLiveSync({ kind: "module", id: moduleId, onUpdate: refetch });
 *   useLiveSync({ kind: "course", id: courseId, onUpdate: refetchList });
 *
 * EventSource is used instead of fetch+ReadableStream because it auto-
 * reconnects with exponential backoff for free. The JWT travels via the
 * `token` query string since EventSource cannot set custom headers.
 */
import { useEffect, useRef } from "react";
import { getToken } from "@/lib/api";

export function useLiveSync({ kind, id, onUpdate }) {
  // Latest callback in a ref so re-renders don't tear down the EventSource.
  const cbRef = useRef(onUpdate);
  useEffect(() => { cbRef.current = onUpdate; }, [onUpdate]);

  useEffect(() => {
    if (!kind) return undefined;
    // `courses` is a global topic (no id). Per-entity topics require an id.
    if (kind !== "courses" && !id) return undefined;
    const token = getToken();
    if (!token) return undefined; // unauthenticated pages can't subscribe

    const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const path = kind === "courses"
      ? `/api/events/courses`
      : `/api/events/${kind}/${encodeURIComponent(id)}`;
    const url = `${backend}${path}?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    const handle = (e) => {
      let payload = {};
      try { payload = JSON.parse(e.data || "{}"); } catch { /* ignore */ }
      // Ignore keepalives — only fire on actual entity changes.
      if (payload?.type === "ping") return;
      cbRef.current?.(payload);
    };

    es.addEventListener("module-updated", handle);
    es.addEventListener("module-created", handle);
    es.addEventListener("module-deleted", handle);
    es.addEventListener("modules-reordered", handle);
    es.addEventListener("course-updated", handle);
    es.addEventListener("course-created", handle);
    es.addEventListener("course-deleted", handle);

    return () => {
      es.removeEventListener("module-updated", handle);
      es.removeEventListener("module-created", handle);
      es.removeEventListener("module-deleted", handle);
      es.removeEventListener("modules-reordered", handle);
      es.removeEventListener("course-updated", handle);
      es.removeEventListener("course-created", handle);
      es.removeEventListener("course-deleted", handle);
      es.close();
    };
  }, [kind, id]);
}
